import { tva } from '@gluestack-ui/utils/nativewind-utils'

import type { TextSize as FigmaBackedTextSize } from '../../theme/figma/figma-typography'
import { TEXT_SIZE_CLASSES } from '../../theme/figma/figma-typography'

/**
 * Named after the Figma variable each step renders — `D1`…`D4`, `H1`,
 * `P1`…`P3`. A design says "P2"; write `size='P2'` and there is nothing to
 * translate.
 *
 * The original values (`body`, `caption`, `heading`, …) still work and resolve
 * to the same class, so nothing has to move at once. They are deprecated, and
 * `h1` is why: it is Figma's D2 (36px), not H1 (18px) — the collision the Figma
 * names remove.
 *
 * `weight` is a separate axis here, which the Figma names do not constrain: a
 * style like `D1` + `semiBold` compiles although Figma defines SemiBold only
 * for P1–P3. Picking a whole text style in one prop is what `aos-ds` is for.
 */
export type TextSize = FigmaBackedTextSize

// Manrope design-system weights. Regular / Medium / SemiBold are the primary
// weights; ExtraLight / Light / Bold cover legacy call sites.
export type TextWeight = 'extraLight' | 'light' | 'regular' | 'medium' | 'semiBold' | 'bold'

export type TextTone =
    | 'default'
    | 'muted'
    | 'disabled'
    | 'on-primary'
    | 'on-graphics'
    | 'logotype'
    | 'knockout'
    | 'link'
    | 'critical'
    | 'caution'
    | 'warning'
    | 'success'
    | 'info'

const textTva = tva({
    variants: {
        // Sourced from the Figma Size/* and Line Height/* variables — see
        // theme/figma/figma-typography.ts. Do not retune by hand.
        size: TEXT_SIZE_CLASSES,
        weight: {
            extraLight: 'font-extraLight',
            light: 'font-light',
            regular: 'font-regular',
            medium: 'font-medium',
            semiBold: 'font-semiBold',
            bold: 'font-bold',
        },
        tone: {
            'default': 'text-aos-text-default',
            'muted': 'text-aos-text-muted',
            'disabled': 'text-aos-text-disabled',
            'on-primary': 'text-aos-text-on-primary',
            'on-graphics': 'text-aos-text-on-graphics',
            'logotype': 'text-aos-text-logotype',
            'knockout': 'text-aos-text-knockout',
            'link': 'text-aos-text-link',
            'critical': 'text-status-critical',
            'caution': 'text-status-caution',
            'warning': 'text-status-warning',
            'success': 'text-status-success',
            'info': 'text-status-info',
        },
    },
    defaultVariants: { size: 'body', weight: 'regular', tone: 'default' },
})

export type TextVariantArgs = {
    size?: TextSize
    weight?: TextWeight
    tone?: TextTone
}

export const textVariants = (args: TextVariantArgs): string => textTva(args)
